/**
 * Usage, model prices, and the AI-use disclosure — `acumon_admin` only.
 *
 * Three panels, and the third is a different kind of thing from the first
 * two. Usage and prices are a record of what happened and what it cost. The
 * AI-use panel is a statement of what the product *can* ask a model to do,
 * which no amount of call history answers: history shows what was used, and
 * the question a client or an insurer asks first is what could be.
 *
 * The figures here are deliberately unrounded to the token and unhidden where
 * they are incomplete. A cost total computed from calls whose model has no
 * price on file is shown with the count of those calls beside it rather than
 * quietly totalling the priced ones — a partial figure a reader can see is a
 * partial figure; one they cannot is a wrong one.
 */

import { useEffect, useState } from 'react';
import type { AiUseEntry } from '@ave/shared';
import {
  api,
  type ModelPrice,
  type UsageByJob,
  type UsageByModel,
  type UsagePushOutcome,
  type UsageReportRow,
} from '../lib/api';
import { Badge, Card, EmptyState, ErrorNotice, SectionHeading, Spinner } from './ui';

const INPUT =
  'mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500';

const today = () => new Date().toISOString().slice(0, 10);

const inForce = (row: { effectiveFrom: string; effectiveTo: string | null }): boolean =>
  row.effectiveFrom <= today() && (row.effectiveTo === null || row.effectiveTo > today());

const tokens = (n: number) => n.toLocaleString('en-GB');

/** Four decimal places: a single classification call costs less than a penny. */
const usd = (n: number | null): string =>
  n === null ? '—' : `$${n.toLocaleString('en-GB', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;

// ---------------------------------------------------------------------------
// Usage
// ---------------------------------------------------------------------------

export function UsagePanel() {
  const [byJob, setByJob] = useState<UsageByJob[] | null>(null);
  const [byModel, setByModel] = useState<UsageByModel[]>([]);
  const [reports, setReports] = useState<UsageReportRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pushing, setPushing] = useState(false);
  const [outcome, setOutcome] = useState<UsagePushOutcome | null>(null);

  async function load() {
    try {
      const u = await api.adminUsage();
      setByJob(u.byJob);
      setByModel(u.byModel);
      setReports(u.reports);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function push() {
    setPushing(true);
    setOutcome(null);
    try {
      setOutcome(await api.pushUsage());
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPushing(false);
    }
  }

  if (error) return <ErrorNotice title="Usage could not be read" message={error} />;
  if (!byJob) return <Spinner label="Reading usage" />;

  const unreported = byJob.reduce((s, r) => s + r.unreported, 0);
  const unpriced = byJob.reduce((s, r) => s + r.unpriced, 0);

  return (
    <div className="space-y-6">
      <Card>
        <SectionHeading
          title="Usage by job and user"
          subtitle="Every model call on this tenancy, grouped by the engagement it was made for and the person who made it. Queue work — an SH01 reading, a document parse — attributes to the engagement it was queued for, so the worker's spend is not invisible."
          right={
            <button
              type="button"
              onClick={() => void push()}
              disabled={pushing || unreported === 0}
              className="rounded-lg bg-teal-600 px-3 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {pushing ? 'Pushing…' : `Push ${unreported} unreported call(s)`}
            </button>
          }
        />

        {outcome ? (
          <div
            className={`mb-4 rounded-xl border px-4 py-3 text-sm ${
              outcome.status === 'sent'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                : 'border-amber-200 bg-amber-50 text-amber-900'
            }`}
          >
            <p className="font-semibold">
              {outcome.status === 'sent'
                ? `Sent ${outcome.batches} batch(es), ${outcome.calls} call(s).`
                : outcome.status === 'dormant'
                  ? 'Nothing was sent — licensing is not configured.'
                  : 'The push failed.'}
            </p>
            {outcome.detail ? <p className="mt-0.5">{outcome.detail}</p> : null}
          </div>
        ) : null}

        {unpriced > 0 ? (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <p className="font-semibold">{unpriced} call(s) have no price on file.</p>
            <p className="mt-0.5">
              Their tokens are counted and their cost is not. Add a price for the model on the
              Model prices tab — it will apply from the date you give it, so a rate backdated to
              before those calls prices them.
            </p>
          </div>
        ) : null}

        {byJob.length === 0 ? (
          <EmptyState
            title="No model calls yet"
            detail="Nothing on this tenancy has invoked a model."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="py-2 pr-3 font-medium">Engagement</th>
                  <th className="py-2 pr-3 font-medium">Who</th>
                  <th className="py-2 pr-3 text-right font-medium">Calls</th>
                  <th className="py-2 pr-3 text-right font-medium">Tokens in</th>
                  <th className="py-2 pr-3 text-right font-medium">Tokens out</th>
                  <th className="py-2 pr-3 text-right font-medium">Cost</th>
                  <th className="py-2 pr-3 font-medium">Reported</th>
                </tr>
              </thead>
              <tbody>
                {byJob.map((r) => (
                  <tr key={`${r.engagementId ?? '-'}|${r.userId ?? '-'}`} className="border-b border-slate-100">
                    <td className="py-2 pr-3">{r.engagementLabel}</td>
                    <td className="py-2 pr-3 text-slate-600">{r.userLabel}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{r.calls}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{tokens(r.tokensInput)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{tokens(r.tokensOutput)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      {usd(r.costUsd)}
                      {r.unpriced > 0 ? (
                        <span className="ml-1 text-xs text-amber-700">+{r.unpriced} unpriced</span>
                      ) : null}
                    </td>
                    <td className="py-2 pr-3">
                      {r.unreported === 0 ? (
                        <Badge tone="emerald">All sent</Badge>
                      ) : (
                        <Badge tone="amber">{r.unreported} waiting</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card>
        <SectionHeading
          title="AI usage by model"
          subtitle="The same calls cut by which model ran them. A model with no price on file appears here by name rather than as a count, because the fix is to price that model."
        />
        {byModel.length === 0 ? (
          <EmptyState title="No model calls yet" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="py-2 pr-3 font-medium">Model</th>
                  <th className="py-2 pr-3 text-right font-medium">Calls</th>
                  <th className="py-2 pr-3 text-right font-medium">Tokens in</th>
                  <th className="py-2 pr-3 text-right font-medium">Tokens out</th>
                  <th className="py-2 pr-3 text-right font-medium">Cost</th>
                  <th className="py-2 pr-3 font-medium">Notes</th>
                </tr>
              </thead>
              <tbody>
                {byModel.map((r) => (
                  <tr key={r.model} className="border-b border-slate-100">
                    <td className="py-2 pr-3 font-mono text-xs">{r.model}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{r.calls}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{tokens(r.tokensInput)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{tokens(r.tokensOutput)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{usd(r.costUsd)}</td>
                    <td className="py-2 pr-3 text-xs">
                      {r.unpriced > 0 ? (
                        <Badge tone="amber">{r.unpriced} unpriced</Badge>
                      ) : null}{' '}
                      {r.notOk > 0 ? <Badge tone="rose">{r.notOk} not ok</Badge> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card>
        <SectionHeading
          title="What was sent to licensing"
          subtitle="The local copy of each push. The remote sink stores what it is given and answers ok, so without this there is no way to answer what we told them we spent except by asking them."
        />
        {reports.length === 0 ? (
          <EmptyState title="Nothing has been pushed yet" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="py-2 pr-3 font-medium">When</th>
                  <th className="py-2 pr-3 font-medium">Engagement</th>
                  <th className="py-2 pr-3 text-right font-medium">Calls</th>
                  <th className="py-2 pr-3 text-right font-medium">Cost</th>
                  <th className="py-2 pr-3 font-medium">Result</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100">
                    <td className="py-2 pr-3 whitespace-nowrap text-slate-600">
                      {new Date(r.createdAt).toLocaleString('en-GB')}
                    </td>
                    <td className="py-2 pr-3">{r.engagementRef ?? '—'}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{r.calls}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{usd(r.costUsd)}</td>
                    <td className="py-2 pr-3">
                      <Badge
                        tone={r.status === 'sent' ? 'emerald' : r.status === 'dormant' ? 'slate' : 'rose'}
                        title={r.detail ?? undefined}
                      >
                        {r.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Model prices
// ---------------------------------------------------------------------------

export function ModelPricesPanel() {
  const [prices, setPrices] = useState<ModelPrice[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    model: '',
    inputPerMillion: '',
    outputPerMillion: '',
    currency: 'USD',
    citation: '',
    effectiveFrom: today(),
  });

  async function load() {
    try {
      setPrices((await api.modelPrices()).prices);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.setModelPrice(form);
      setForm({ ...form, model: '', inputPerMillion: '', outputPerMillion: '', citation: '' });
      await load();
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  if (!prices && !error) return <Spinner label="Reading prices" />;

  return (
    <div className="space-y-6">
      {error ? <ErrorNotice title="Price could not be saved" message={error} /> : null}

      <Card>
        <SectionHeading
          title="Model prices"
          subtitle="List prices per million tokens. A call is costed at the price in force on the day it was made, so adding a rate here never restates a total already reported — and a rate backdated to before an unpriced call is how that call gets a cost."
        />

        <form onSubmit={(e) => void save(e)} className="mb-6 grid gap-3 sm:grid-cols-3">
          <label className="text-sm sm:col-span-2">
            <span className="font-medium text-slate-700">Model id</span>
            <input
              className={INPUT}
              required
              placeholder="claude-opus-5"
              value={form.model}
              onChange={(e) => setForm({ ...form, model: e.target.value })}
            />
          </label>
          <label className="text-sm">
            <span className="font-medium text-slate-700">Effective from</span>
            <input
              type="date"
              className={INPUT}
              required
              value={form.effectiveFrom}
              onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })}
            />
          </label>
          <label className="text-sm">
            <span className="font-medium text-slate-700">Input, per million</span>
            <input
              className={INPUT}
              required
              placeholder="5.00"
              value={form.inputPerMillion}
              onChange={(e) => setForm({ ...form, inputPerMillion: e.target.value })}
            />
          </label>
          <label className="text-sm">
            <span className="font-medium text-slate-700">Output, per million</span>
            <input
              className={INPUT}
              required
              placeholder="25.00"
              value={form.outputPerMillion}
              onChange={(e) => setForm({ ...form, outputPerMillion: e.target.value })}
            />
          </label>
          <label className="text-sm">
            <span className="font-medium text-slate-700">Currency</span>
            <input
              className={INPUT}
              required
              maxLength={3}
              value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
            />
          </label>
          <label className="text-sm sm:col-span-3">
            <span className="font-medium text-slate-700">Where the rate came from</span>
            <input
              className={INPUT}
              required
              placeholder="Anthropic published list price, read 17 August 2026"
              value={form.citation}
              onChange={(e) => setForm({ ...form, citation: e.target.value })}
            />
            <span className="mt-1 block text-xs text-slate-500">
              Required. A price with no source is a number somebody typed, and every cost total is
              computed from it.
            </span>
          </label>
          <div className="sm:col-span-3">
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white disabled:bg-slate-300"
            >
              {saving ? 'Saving…' : 'Set price'}
            </button>
          </div>
        </form>

        {!prices || prices.length === 0 ? (
          <EmptyState title="No prices on file" detail="Every call will report tokens and no cost." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="py-2 pr-3 font-medium">Model</th>
                  <th className="py-2 pr-3 text-right font-medium">In / M</th>
                  <th className="py-2 pr-3 text-right font-medium">Out / M</th>
                  <th className="py-2 pr-3 font-medium">In force</th>
                  <th className="py-2 pr-3 font-medium">Source</th>
                </tr>
              </thead>
              <tbody>
                {prices.map((p) => (
                  <tr
                    key={p.id}
                    className={`border-b border-slate-100 ${inForce(p) ? '' : 'text-slate-400'}`}
                  >
                    <td className="py-2 pr-3 font-mono text-xs">{p.model}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      {p.currency} {p.inputPerMillion}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      {p.currency} {p.outputPerMillion}
                    </td>
                    <td className="py-2 pr-3 whitespace-nowrap">
                      {p.effectiveFrom} → {p.effectiveTo ?? 'current'}{' '}
                      {inForce(p) ? <Badge tone="emerald">in force</Badge> : null}
                    </td>
                    <td className="py-2 pr-3 text-xs text-slate-500">{p.citation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AI use
// ---------------------------------------------------------------------------

export function AiUsePanel() {
  const [data, setData] = useState<{
    boundary: { statement: string; enforcedBy: string };
    entries: AiUseEntry[];
    models: { default: string; escalation: string };
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .aiUse()
      .then(setData)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, []);

  if (error) return <ErrorNotice title="The disclosure could not be read" message={error} />;
  if (!data) return <Spinner label="Reading the disclosure" />;

  const wired = data.entries.filter((e) => e.wired);
  const unwired = data.entries.filter((e) => !e.wired);

  return (
    <div className="space-y-6">
      <Card>
        <SectionHeading
          title="Where AI can be used in this tool"
          subtitle="What the product is capable of asking a model to do — not a record of what it has asked. Usage is on the Usage tab; this answers the question a client or an insurer asks before the first engagement."
        />
        <div className="rounded-xl border border-teal-200 bg-teal-50 px-4 py-3">
          <p className="text-sm font-semibold text-teal-900">The boundary</p>
          <p className="mt-1 text-sm text-teal-900">{data.boundary.statement}</p>
          <p className="mt-1 font-mono text-xs text-teal-700">Enforced by {data.boundary.enforcedBy}</p>
        </div>
        <p className="mt-4 text-sm text-slate-600">
          The tiers below resolve to <span className="font-mono text-xs">{data.models.default}</span>{' '}
          (default) and{' '}
          <span className="font-mono text-xs">{data.models.escalation}</span> (escalation). Templates
          name a tier rather than a model id so the two cannot drift apart.
        </p>
      </Card>

      <AiUseTable title="In use" entries={wired} />

      {unwired.length > 0 ? (
        <AiUseTable
          title="Built and reachable, but nothing calls it"
          subtitle="These tasks are deployed and routable at the orchestrator, and no screen or job in this product invokes them. They are listed because a capability that exists and is unused is exactly what a disclosure is for — leaving them out would make this a description of current traffic rather than of what the tool can do."
          entries={unwired}
          muted
        />
      ) : null}
    </div>
  );
}

function AiUseTable({
  title,
  subtitle,
  entries,
  muted = false,
}: {
  title: string;
  subtitle?: string;
  entries: AiUseEntry[];
  muted?: boolean;
}) {
  if (entries.length === 0) return null;
  return (
    <Card>
      <SectionHeading title={title} subtitle={subtitle} />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="py-2 pr-3 font-medium">Feature / purpose</th>
              <th className="py-2 pr-3 font-medium">Where it fires</th>
              <th className="py-2 pr-3 font-medium">Provider</th>
              <th className="py-2 pr-3 font-medium">Data processing</th>
              <th className="py-2 pr-3 font-medium">Model</th>
              <th className="py-2 pr-3 font-medium">Role / usage</th>
              <th className="py-2 pr-3 font-medium">Fallback</th>
              <th className="py-2 pr-3 font-medium">Source</th>
            </tr>
          </thead>
          <tbody className={muted ? 'text-slate-500' : ''}>
            {entries.map((e) => (
              <tr key={e.promptTemplate} className="border-b border-slate-100 align-top">
                <td className="py-3 pr-3 font-medium text-slate-800">{e.feature}</td>
                <td className="py-3 pr-3">{e.trigger}</td>
                <td className="py-3 pr-3">
                  <Badge tone="violet">{e.provider}</Badge>
                </td>
                <td className="py-3 pr-3">{e.dataProcessing}</td>
                <td className="py-3 pr-3">
                  <div className="font-mono text-xs">{e.modelTier}</div>
                  <div className="mt-1 font-mono text-xs text-slate-400">{e.promptTemplate}</div>
                </td>
                <td className="py-3 pr-3">{e.role}</td>
                <td className="py-3 pr-3">{e.fallback}</td>
                <td className="py-3 pr-3 font-mono text-xs text-slate-500">{e.source}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
